// Algorithm step generators — each returns an array of events consumed by the store.
// emit() is synchronous; all steps are pre-computed before playback begins.

function emit(steps, event) {
  steps.push(event)
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function initArray(steps, arr, operation, explanation) {
  // Snapshot the array — callers (e.g. the sorts) mutate their copy in place
  // while generating steps, so we must freeze its values at init time.
  emit(steps, { type: 'init', elements: [...arr], operation, explanation })
}

function setActive(steps, indices, op, exp) {
  emit(steps, { type: 'set-state', indices, state: 'active', operation: op, explanation: exp })
}

function setCompared(steps, indices, op, exp) {
  emit(steps, { type: 'set-state', indices, state: 'compared', operation: op, explanation: exp })
}

function setVisited(steps, indices, op, exp) {
  emit(steps, { type: 'set-state', indices, state: 'visited', operation: op, explanation: exp })
}

function setAnswer(steps, indices, op, exp) {
  emit(steps, { type: 'set-state', indices, state: 'answer', operation: op, explanation: exp })
}

function setRejected(steps, indices, op, exp) {
  emit(steps, { type: 'set-state', indices, state: 'rejected', operation: op, explanation: exp })
}

function clearHighlights(steps) {
  emit(steps, { type: 'clear-state', clearStates: ['active', 'compared'] })
}

function pointer(steps, name, index, vars, op, exp) {
  emit(steps, { type: 'pointer', name, index, operation: op, explanation: exp })
  if (vars) emit(steps, { type: 'vars', vars })
}

function removePointer(steps, name) {
  emit(steps, { type: 'pointer', name, remove: true })
}

function setVars(steps, vars, op, exp) {
  emit(steps, { type: 'vars', vars, operation: op, explanation: exp })
}

function doSwap(steps, i, j, op, exp) {
  emit(steps, { type: 'swap', indices: [i, j], operation: op, explanation: exp })
}

// ─── Array Traversal ────────────────────────────────────────────────────────

export function arrayTraversal(nums) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Traversing array of ${nums.length} elements`)
  for (let i = 0; i < nums.length; i++) {
    setActive(steps, [i], `arr[${i}]`, `Visiting element at index ${i}: value = ${nums[i]}`)
    pointer(steps, 'i', i, { i }, `i = ${i}`, `Current index is ${i}, value is ${nums[i]}`)
    setVisited(steps, [i], `Visited arr[${i}]`, `Mark index ${i} as visited`)
  }
  setAnswer(steps, nums.map((_, i) => i), 'Traversal complete', 'All elements have been visited in O(n) time')
  return steps
}

// ─── Two Sum ────────────────────────────────────────────────────────────────

export function twoSum(nums, target) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Find two numbers in [${nums.join(', ')}] that sum to ${target}`)
  setVars(steps, { target, hashMap: '{}' }, 'Create hash map', 'We store each value→index in a hash map for O(1) lookup')

  const map = {}
  for (let i = 0; i < nums.length; i++) {
    setActive(steps, [i], `Check nums[${i}] = ${nums[i]}`, `At index ${i}, current value is ${nums[i]}`)
    pointer(steps, 'i', i, { i, 'nums[i]': nums[i] })

    const complement = target - nums[i]
    setVars(steps, { complement }, `complement = ${target} − ${nums[i]} = ${complement}`, `We need to find ${complement} in the hash map`)

    if (map[complement] !== undefined) {
      setAnswer(steps, [map[complement], i],
        `Return [${map[complement]}, ${i}]`,
        `Found ${complement} at index ${map[complement]}! nums[${map[complement]}] + nums[${i}] = ${nums[map[complement]]} + ${nums[i]} = ${target} ✓`
      )
      removePointer(steps, 'i')
      break
    }

    setVars(steps, { [`map[${nums[i]}]`]: i }, `map[${nums[i]}] = ${i}`, `${complement} not found. Store ${nums[i]}→${i} in map for future lookups`)
    map[nums[i]] = i
    setVisited(steps, [i], `Stored ${nums[i]}`, `Index ${i} stored in hash map`)
  }
  return steps
}

// ─── Sliding Window (max sum of k elements) ─────────────────────────────────

export function slidingWindow(nums, k) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Find the maximum subarray sum of size ${k}`)

  let windowSum = nums.slice(0, k).reduce((a, b) => a + b, 0)
  let maxSum = windowSum

  // First window
  const firstWin = Array.from({ length: k }, (_, i) => i)
  setActive(steps, firstWin, `Initial window [0..${k - 1}]`, `Compute first window sum = ${windowSum}`)
  setVars(steps, { windowSum, maxSum, left: 0, right: k - 1 }, 'First window', `Sum of first ${k} elements is ${windowSum}`)
  pointer(steps, 'L', 0)
  pointer(steps, 'R', k - 1)

  for (let r = k; r < nums.length; r++) {
    const l = r - k + 1
    windowSum = windowSum - nums[l - 1] + nums[r]
    if (windowSum > maxSum) maxSum = windowSum

    clearHighlights(steps)
    setActive(steps, Array.from({ length: k }, (_, i) => l + i), `Window [${l}..${r}]`, `Slide right: remove ${nums[l - 1]}, add ${nums[r]}. New sum = ${windowSum}`)
    pointer(steps, 'L', l, { L: l, R: r, windowSum, maxSum })
    pointer(steps, 'R', r, { L: l, R: r, windowSum, maxSum })
    setVars(steps, { left: l, right: r, windowSum, maxSum }, `windowSum = ${windowSum}`, `Current sum ${windowSum}${windowSum === maxSum ? ' — new maximum!' : ''}`)
  }

  // Find best window
  let bestL = 0
  let bestSum = nums.slice(0, k).reduce((a, b) => a + b, 0)
  for (let l = 1; l <= nums.length - k; l++) {
    const s = nums.slice(l, l + k).reduce((a, b) => a + b, 0)
    if (s > bestSum) { bestSum = s; bestL = l }
  }
  const answerIndices = Array.from({ length: k }, (_, i) => bestL + i)
  clearHighlights(steps)
  setAnswer(steps, answerIndices, `Max sum = ${maxSum}`, `Maximum subarray of size ${k} starts at index ${bestL} with sum ${maxSum}`)
  return steps
}

// ─── Bubble Sort ────────────────────────────────────────────────────────────

export function bubbleSort(arr) {
  const steps = []
  const a = [...arr]
  initArray(steps, a, 'Initialize', `Bubble Sort on [${a.join(', ')}] — largest bubbles to end each pass`)

  let swapped
  let pass = 0
  const n = a.length

  do {
    swapped = false
    emit(steps, { type: 'vars', vars: { pass: pass + 1 }, operation: `Pass ${pass + 1}`, explanation: `Starting pass ${pass + 1} — unsorted region is [0..${n - 1 - pass}]` })

    for (let j = 0; j < n - 1 - pass; j++) {
      setCompared(steps, [j, j + 1], `Compare arr[${j}] and arr[${j + 1}]`, `Is ${a[j]} > ${a[j + 1]}?`)
      pointer(steps, 'j', j, { j, 'a[j]': a[j], 'a[j+1]': a[j + 1] })

      if (a[j] > a[j + 1]) {
        setActive(steps, [j, j + 1], `Swap ${a[j]} ↔ ${a[j + 1]}`, `${a[j]} > ${a[j + 1]}, swapping them`)
        ;[a[j], a[j + 1]] = [a[j + 1], a[j]]
        doSwap(steps, j, j + 1, `arr[${j}] ↔ arr[${j + 1}]`, `Swapped — ${a[j]} now at index ${j}`)
        swapped = true
      } else {
        clearHighlights(steps)
        emit(steps, { explanation: `${a[j]} ≤ ${a[j + 1]}, no swap needed` })
      }
    }
    // Mark last element of this pass as sorted
    setAnswer(steps, [n - 1 - pass], `arr[${n - 1 - pass}] is sorted`, `${a[n - 1 - pass]} is in its final position`)
    pass++
  } while (swapped && pass < n - 1)

  // Mark all remaining as sorted
  const remaining = Array.from({ length: n - pass }, (_, i) => i)
  if (remaining.length > 0) setAnswer(steps, remaining, 'Sorted!', 'Array is fully sorted')
  return steps
}

// ─── Selection Sort ─────────────────────────────────────────────────────────

export function selectionSort(arr) {
  const steps = []
  const a = [...arr]
  initArray(steps, a, 'Initialize', `Selection Sort — find minimum each pass, place it at front`)

  for (let i = 0; i < a.length - 1; i++) {
    let minIdx = i
    pointer(steps, 'i', i, { i })
    setActive(steps, [i], `Unsorted start = ${i}`, `Assume index ${i} (value ${a[i]}) is the minimum`)
    setVars(steps, { minIdx: i, 'a[minIdx]': a[i] }, `minIdx = ${i}`, `Looking for the minimum in unsorted region [${i}..${a.length - 1}]`)

    for (let j = i + 1; j < a.length; j++) {
      setCompared(steps, [j, minIdx], `Compare arr[${j}] with min`, `Is ${a[j]} < ${a[minIdx]} (current min)?`)
      pointer(steps, 'j', j, { j, 'a[j]': a[j] })

      if (a[j] < a[minIdx]) {
        setActive(steps, [j], `New min: ${a[j]}`, `${a[j]} < ${a[minIdx]} — new minimum found at index ${j}`)
        minIdx = j
        setVars(steps, { minIdx: j, 'a[minIdx]': a[j] }, `minIdx = ${j}`, `Updated minimum to ${a[j]} at index ${j}`)
      } else {
        clearHighlights(steps)
      }
    }

    if (minIdx !== i) {
      ;[a[i], a[minIdx]] = [a[minIdx], a[i]]
      doSwap(steps, i, minIdx, `Swap arr[${i}] ↔ arr[${minIdx}]`, `Place minimum at index ${i}`)
    }
    setAnswer(steps, [i], `arr[${i}] sorted`, `${a[i]} is in its final position`)
    removePointer(steps, 'j')
    clearHighlights(steps)
  }
  setAnswer(steps, Array.from({ length: a.length }, (_, i) => i), 'Sorted!', 'Array fully sorted')
  return steps
}

// ─── Binary Search ──────────────────────────────────────────────────────────

export function binarySearch(arr, target) {
  const steps = []
  const a = [...arr].sort((x, y) => x - y)
  initArray(steps, a, 'Initialize', `Binary Search for ${target} in sorted array [${a.join(', ')}]`)
  setVars(steps, { target, left: 0, right: a.length - 1 }, 'Set boundaries', 'Search space covers the full array')
  pointer(steps, 'L', 0)
  pointer(steps, 'R', a.length - 1)

  let left = 0, right = a.length - 1
  let found = false
  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    pointer(steps, 'mid', mid, { left, mid, right, 'a[mid]': a[mid] }, `mid = (${left}+${right})÷2 = ${mid}`, `Checking middle element: a[${mid}] = ${a[mid]}`)
    setActive(steps, [mid], `a[mid] = ${a[mid]}`, `Comparing ${a[mid]} with target ${target}`)

    if (a[mid] === target) {
      setAnswer(steps, [mid], `Found at index ${mid}!`, `${a[mid]} === ${target} — target found at index ${mid} ✓`)
      found = true
      break
    } else if (a[mid] < target) {
      setRejected(steps, Array.from({ length: mid - left + 1 }, (_, i) => left + i),
        `Search right half`, `${a[mid]} < ${target} — discard left half [${left}..${mid}]`)
      left = mid + 1
      pointer(steps, 'L', left, { left, right }, `left = ${left}`, `New left boundary: ${left}`)
    } else {
      setRejected(steps, Array.from({ length: right - mid + 1 }, (_, i) => mid + i),
        `Search left half`, `${a[mid]} > ${target} — discard right half [${mid}..${right}]`)
      right = mid - 1
      pointer(steps, 'R', right, { left, right }, `right = ${right}`, `New right boundary: ${right}`)
    }
  }
  if (!found) {
    const allIdx = Array.from({ length: a.length }, (_, i) => i)
    setRejected(steps, allIdx, 'Not found', `${target} does not exist in the array`)
  }
  return steps
}

// ─── Merge Sort ─────────────────────────────────────────────────────────────

export function mergeSort(arr) {
  const steps = []
  const a = [...arr]
  initArray(steps, a, 'Initialize', `Merge Sort — divide array in half recursively, then merge sorted halves`)

  function merge(array, left, mid, right) {
    const L = array.slice(left, mid + 1)
    const R = array.slice(mid + 1, right + 1)
    let i = 0, j = 0, k = left

    while (i < L.length && j < R.length) {
      setCompared(steps, [left + i, mid + 1 + j], `Compare ${L[i]} vs ${R[j]}`, `Left: ${L[i]}, Right: ${R[j]}`)
      if (L[i] <= R[j]) {
        array[k] = L[i++]
      } else {
        array[k] = R[j++]
      }
      emit(steps, { type: 'replace-elements', elements: [...array], operation: `Place ${array[k]}`, explanation: `Place smaller element at position ${k}` })
      k++
    }
    while (i < L.length) { array[k++] = L[i++] }
    while (j < R.length) { array[k++] = R[j++] }
    const merged = Array.from({ length: right - left + 1 }, (_, idx) => left + idx)
    setAnswer(steps, merged, `Merged [${left}..${right}]`, `Subarray [${left}..${right}] is sorted`)
  }

  function sort(array, left, right) {
    if (left >= right) return
    const mid = Math.floor((left + right) / 2)
    setActive(steps, Array.from({ length: right - left + 1 }, (_, i) => left + i),
      `Divide [${left}..${right}] at mid ${mid}`, `Split into [${left}..${mid}] and [${mid + 1}..${right}]`)
    sort(array, left, mid)
    sort(array, mid + 1, right)
    merge(array, left, mid, right)
  }

  sort(a, 0, a.length - 1)
  setAnswer(steps, Array.from({ length: a.length }, (_, i) => i), 'Sorted!', 'Merge sort complete — array is fully sorted')
  return steps
}

// ─── Quick Sort ─────────────────────────────────────────────────────────────

export function quickSort(arr) {
  const steps = []
  const a = [...arr]
  initArray(steps, a, 'Initialize', `Quick Sort — pick pivot, partition around it, recurse`)

  function partition(array, low, high) {
    const pivot = array[high]
    emit(steps, { type: 'set-state', indices: [high], state: 'pivot', operation: `Pivot = ${pivot}`, explanation: `Choose ${pivot} as pivot (last element)` })
    let i = low - 1
    pointer(steps, 'i', i, { i, pivot })

    for (let j = low; j < high; j++) {
      setCompared(steps, [j, high], `arr[${j}] vs pivot ${pivot}`, `Is ${array[j]} ≤ ${pivot}?`)
      pointer(steps, 'j', j, { j, 'a[j]': array[j] })

      if (array[j] <= pivot) {
        i++
        ;[array[i], array[j]] = [array[j], array[i]]
        doSwap(steps, i, j, `Swap arr[${i}] ↔ arr[${j}]`, `${array[i]} ≤ pivot — move to left partition`)
      } else {
        clearHighlights(steps)
      }
    }
    ;[array[i + 1], array[high]] = [array[high], array[i + 1]]
    doSwap(steps, i + 1, high, `Place pivot`, `Move pivot to its final position ${i + 1}`)
    setAnswer(steps, [i + 1], `Pivot ${pivot} at index ${i + 1}`, `Pivot is in its correct sorted position`)
    return i + 1
  }

  function sort(array, low, high) {
    if (low >= high) return
    const pi = partition(array, low, high)
    sort(array, low, pi - 1)
    sort(array, pi + 1, high)
  }

  sort(a, 0, a.length - 1)
  setAnswer(steps, Array.from({ length: a.length }, (_, i) => i), 'Sorted!', 'Quick sort complete')
  return steps
}

// ─── Stack ──────────────────────────────────────────────────────────────────

export function stackOps(operations) {
  // operations: [{op: 'push', val: 5}, {op: 'pop'}, ...]
  const steps = []
  initArray(steps, [], 'Initialize Stack', 'Empty stack — LIFO (Last In First Out)')

  const stack = []
  for (const { op, val } of operations) {
    if (op === 'push') {
      stack.push(val)
      emit(steps, { type: 'replace-elements', elements: [...stack] })
      setActive(steps, [stack.length - 1], `Push ${val}`, `Push ${val} onto the top of the stack. Size: ${stack.length}`)
      setVars(steps, { top: stack.length - 1, size: stack.length }, `top = ${stack.length - 1}`, `Top of stack is now index ${stack.length - 1}`)
    } else if (op === 'pop') {
      if (stack.length === 0) {
        emit(steps, { type: 'vars', vars: {}, operation: 'Pop (empty)', explanation: 'Cannot pop from empty stack!' })
        continue
      }
      const popped = stack.pop()
      setActive(steps, [stack.length], `Pop → ${popped}`, `Remove ${popped} from top of stack`)
      emit(steps, { type: 'replace-elements', elements: [...stack] })
      setVars(steps, { top: stack.length - 1, size: stack.length, popped }, `top = ${stack.length - 1}`, `${popped} removed. Stack size: ${stack.length}`)
    }
  }
  if (stack.length > 0)
    setAnswer(steps, Array.from({ length: stack.length }, (_, i) => i), 'Final stack', `Stack contains [${stack.join(', ')}]`)
  return steps
}

// ─── Queue ──────────────────────────────────────────────────────────────────

export function queueOps(operations) {
  const steps = []
  initArray(steps, [], 'Initialize Queue', 'Empty queue — FIFO (First In First Out)')

  const queue = []
  for (const { op, val } of operations) {
    if (op === 'enqueue') {
      queue.push(val)
      emit(steps, { type: 'replace-elements', elements: [...queue] })
      setActive(steps, [queue.length - 1], `Enqueue ${val}`, `Add ${val} to the back of the queue`)
      pointer(steps, 'rear', queue.length - 1, { front: 0, rear: queue.length - 1, size: queue.length })
    } else if (op === 'dequeue') {
      if (queue.length === 0) continue
      const dequeued = queue.shift()
      setActive(steps, [0], `Dequeue → ${dequeued}`, `Remove ${dequeued} from the front of the queue`)
      emit(steps, { type: 'replace-elements', elements: [...queue] })
      setVars(steps, { front: 0, rear: queue.length - 1, dequeued, size: queue.length }, `dequeued = ${dequeued}`, `${dequeued} removed from front. Queue size: ${queue.length}`)
    }
  }
  if (queue.length > 0)
    setAnswer(steps, Array.from({ length: queue.length }, (_, i) => i), 'Final queue', `Queue contains [${queue.join(', ')}]`)
  return steps
}

// ─── Linked List Traversal ──────────────────────────────────────────────────

export function linkedListTraversal(values) {
  const steps = []
  initArray(steps, values, 'Initialize', `Linked list: ${values.map((v, i) => `${v}${i < values.length - 1 ? ' → ' : ' → null'}`).join('')}`)
  setVars(steps, { current: 'head', head: values[0] }, 'current = head', 'Start at head node')
  pointer(steps, 'current', 0, { current: `node(${values[0]})` })

  for (let i = 0; i < values.length; i++) {
    setActive(steps, [i], `Visit node ${values[i]}`, `current.value = ${values[i]}`)
    setVars(steps, { current: `node(${values[i]})`, 'current.val': values[i], next: i < values.length - 1 ? `node(${values[i + 1]})` : 'null' },
      `process node ${values[i]}`, `Node value: ${values[i]}, next: ${i < values.length - 1 ? values[i + 1] : 'null'}`)
    setVisited(steps, [i], `node ${values[i]} visited`, `Move to next node`)
    if (i < values.length - 1) {
      pointer(steps, 'current', i + 1, { current: `node(${values[i + 1]})` }, `current = current.next`, `Advance pointer to next node`)
    }
  }
  setAnswer(steps, Array.from({ length: values.length }, (_, i) => i), 'Traversal complete', `Visited all ${values.length} nodes, reached null — end of list`)
  return steps
}

// ════════════════════════════════════════════════════════════════════════════
// Grind 75 — Easy tier (reuse array / stack / linked-list renderers)
// ════════════════════════════════════════════════════════════════════════════

// ─── #217 Contains Duplicate ────────────────────────────────────────────────

export function containsDuplicate(nums) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Detect duplicates in [${nums.join(', ')}] using a hash set`)
  setVars(steps, { seen: '{ }' }, 'Create hash set', 'Track values we have already encountered')

  const seen = {}
  for (let i = 0; i < nums.length; i++) {
    setActive(steps, [i], `Check nums[${i}] = ${nums[i]}`, `Have we already seen ${nums[i]}?`)
    pointer(steps, 'i', i, { i, 'nums[i]': nums[i] })

    if (seen[nums[i]] !== undefined) {
      setAnswer(steps, [seen[nums[i]], i], `Duplicate ${nums[i]} found`,
        `${nums[i]} already appeared at index ${seen[nums[i]]} — a duplicate exists ✓`)
      removePointer(steps, 'i')
      return steps
    }
    setVars(steps, { [`seen[${nums[i]}]`]: i }, `add ${nums[i]}`, `${nums[i]} is new — store it in the set`)
    seen[nums[i]] = i
    setVisited(steps, [i], `stored ${nums[i]}`, `Index ${i} marked as seen`)
  }
  setRejected(steps, nums.map((_, i) => i), 'No duplicates', 'Every value is unique — no duplicate found')
  return steps
}

// ─── #121 Best Time to Buy and Sell Stock ───────────────────────────────────

export function bestTimeToBuySell(prices) {
  const steps = []
  initArray(steps, prices, 'Initialize', `Maximise profit from a single buy then sell in [${prices.join(', ')}]`)
  setVars(steps, { minPrice: '∞', maxProfit: 0 }, 'Init', 'Track the lowest price so far and the best profit')

  let minPrice = Infinity, maxProfit = 0, buyIdx = 0, bestBuy = 0, bestSell = 0
  for (let i = 0; i < prices.length; i++) {
    setActive(steps, [i], `Day ${i}: price ${prices[i]}`, `Consider the price ${prices[i]} on day ${i}`)
    pointer(steps, 'i', i, { i, price: prices[i] })

    if (prices[i] < minPrice) {
      minPrice = prices[i]; buyIdx = i
      pointer(steps, 'buy', i)
      setVars(steps, { minPrice, maxProfit }, `new min ${minPrice}`, `${prices[i]} is the cheapest so far — best day to buy`)
    } else {
      const profit = prices[i] - minPrice
      if (profit > maxProfit) { maxProfit = profit; bestBuy = buyIdx; bestSell = i }
      setVars(steps, { minPrice, profit, maxProfit }, `profit ${profit}`,
        `Sell at ${prices[i]} → profit ${prices[i]} − ${minPrice} = ${profit}${profit === maxProfit ? ' (best so far!)' : ''}`)
    }
  }
  setAnswer(steps, [bestBuy, bestSell], `Max profit ${maxProfit}`,
    `Buy on day ${bestBuy} (${prices[bestBuy]}), sell on day ${bestSell} (${prices[bestSell]}) → profit ${maxProfit}`)
  return steps
}

// ─── #125 Valid Palindrome ──────────────────────────────────────────────────

export function validPalindrome(str) {
  const steps = []
  const chars = str.split('')
  initArray(steps, chars, 'Initialize', `Is "${str}" a palindrome? Compare characters from both ends inward`)

  let l = 0, r = chars.length - 1
  pointer(steps, 'L', l)
  pointer(steps, 'R', r)
  while (l < r) {
    setCompared(steps, [l, r], `Compare '${chars[l]}' and '${chars[r]}'`, `Does position ${l} match position ${r}?`)
    pointer(steps, 'L', l, { L: l, R: r })
    pointer(steps, 'R', r, { L: l, R: r })

    if (chars[l].toLowerCase() !== chars[r].toLowerCase()) {
      setRejected(steps, [l, r], `'${chars[l]}' ≠ '${chars[r]}'`, `Mismatch — "${str}" is NOT a palindrome`)
      return steps
    }
    setVisited(steps, [l, r], `'${chars[l]}' = '${chars[r]}'`, `Match — move both pointers inward`)
    l++; r--
  }
  setAnswer(steps, chars.map((_, i) => i), 'Palindrome ✓', `All characters matched — "${str}" reads the same both ways`)
  return steps
}

// ─── #169 Majority Element (Boyer–Moore voting) ─────────────────────────────

export function majorityElement(nums) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Find the element appearing > n/2 times via Boyer–Moore voting`)

  let candidate = null, count = 0
  for (let i = 0; i < nums.length; i++) {
    pointer(steps, 'i', i, { i, candidate: candidate === null ? '—' : candidate, count })
    if (count === 0) {
      candidate = nums[i]; count = 1
      setActive(steps, [i], `candidate = ${candidate}`, `Count was 0 — adopt ${nums[i]} as the new candidate (count = 1)`)
      setVars(steps, { candidate, count })
    } else if (nums[i] === candidate) {
      count++
      setActive(steps, [i], `count++ → ${count}`, `${nums[i]} matches the candidate — count rises to ${count}`)
      setVars(steps, { candidate, count })
    } else {
      count--
      setCompared(steps, [i], `count-- → ${count}`, `${nums[i]} differs from candidate ${candidate} — count drops to ${count}`)
      setVars(steps, { candidate, count })
    }
    setVisited(steps, [i])
  }
  const idxs = nums.map((v, i) => (v === candidate ? i : -1)).filter((i) => i >= 0)
  setAnswer(steps, idxs, `Majority = ${candidate}`, `${candidate} survives the voting — it is the majority element`)
  return steps
}

// ─── #278 First Bad Version (binary search) ─────────────────────────────────

export function firstBadVersion(n, firstBad) {
  const steps = []
  const versions = Array.from({ length: n }, (_, i) => i + 1)
  initArray(steps, versions, 'Initialize', `${n} versions — once one is bad, all later ones are too. Binary-search the first bad version`)
  setVars(steps, { firstBad: '?' }, 'Setup', 'isBadVersion(v) reports whether a version is bad')

  let lo = 0, hi = n - 1, ans = -1
  pointer(steps, 'L', lo)
  pointer(steps, 'R', hi)
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2)
    const bad = versions[mid] >= firstBad
    pointer(steps, 'mid', mid, { lo: lo + 1, mid: versions[mid], hi: hi + 1 })
    setActive(steps, [mid], `Check version ${versions[mid]}`, `isBadVersion(${versions[mid]}) = ${bad}`)

    if (bad) {
      ans = mid
      setRejected(steps, Array.from({ length: hi - mid }, (_, i) => mid + 1 + i),
        `v${versions[mid]} bad → go left`, `${versions[mid]} is bad — the first bad is at or before it. Discard the right half`)
      hi = mid - 1
      pointer(steps, 'R', hi)
    } else {
      setRejected(steps, Array.from({ length: mid - lo + 1 }, (_, i) => lo + i),
        `v${versions[mid]} good → go right`, `${versions[mid]} is good — the first bad must be later. Discard the left half`)
      lo = mid + 1
      pointer(steps, 'L', lo)
    }
  }
  setAnswer(steps, [ans], `First bad = version ${versions[ans]}`, `Version ${versions[ans]} is the first bad version`)
  return steps
}

// ─── #70 Climbing Stairs (dynamic programming) ──────────────────────────────

export function climbingStairs(n) {
  const steps = []
  const dp = new Array(n + 1).fill(0)
  initArray(steps, dp, 'Initialize', `Count ways to climb ${n} stairs taking 1 or 2 steps. dp[i] = dp[i-1] + dp[i-2]`)

  dp[0] = 1
  emit(steps, { type: 'replace-elements', elements: [...dp] })
  setActive(steps, [0], 'dp[0] = 1', 'Base case: exactly 1 way to stand at the ground')
  if (n >= 1) {
    dp[1] = 1
    emit(steps, { type: 'replace-elements', elements: [...dp] })
    setActive(steps, [1], 'dp[1] = 1', 'Base case: 1 way to climb a single stair')
  }
  for (let i = 2; i <= n; i++) {
    setCompared(steps, [i - 1, i - 2], `dp[${i - 1}] + dp[${i - 2}]`,
      `Ways to reach stair ${i} = ways to ${i - 1} (${dp[i - 1]}) + ways to ${i - 2} (${dp[i - 2]})`)
    dp[i] = dp[i - 1] + dp[i - 2]
    emit(steps, { type: 'replace-elements', elements: [...dp] })
    setActive(steps, [i], `dp[${i}] = ${dp[i]}`, `dp[${i}] = ${dp[i - 1]} + ${dp[i - 2]} = ${dp[i]}`)
    setVars(steps, { [`dp[${i}]`]: dp[i] })
  }
  setAnswer(steps, [n], `Answer = ${dp[n]}`, `There are ${dp[n]} distinct ways to climb ${n} stairs`)
  return steps
}

// ─── #20 Valid Parentheses (stack) ──────────────────────────────────────────

export function validParentheses(str) {
  const steps = []
  initArray(steps, [], 'Initialize', `Validate the brackets in "${str}" with a stack`)
  const match = { ')': '(', ']': '[', '}': '{' }
  const stack = []

  for (let k = 0; k < str.length; k++) {
    const ch = str[k]
    if (ch === '(' || ch === '[' || ch === '{') {
      stack.push(ch)
      emit(steps, { type: 'replace-elements', elements: [...stack] })
      setActive(steps, [stack.length - 1], `Push '${ch}'`, `'${ch}' is an opening bracket — push it onto the stack`)
      setVars(steps, { scanning: `'${ch}'`, stack: `[${stack.join('')}]` })
    } else {
      setVars(steps, { scanning: `'${ch}'`, need: `'${match[ch]}'`, stack: `[${stack.join('') || ' '}]` },
        `See '${ch}'`, `'${ch}' is a closing bracket — the top of the stack must be '${match[ch]}'`)
      if (stack.length === 0 || stack[stack.length - 1] !== match[ch]) {
        if (stack.length > 0) {
          setRejected(steps, [stack.length - 1], 'Mismatch',
            `Top is '${stack[stack.length - 1]}' but '${ch}' needs '${match[ch]}' — INVALID`)
        } else {
          emit(steps, { operation: 'Empty stack', explanation: `No opening bracket to match '${ch}' — INVALID` })
        }
        return steps
      }
      const popped = stack.pop()
      setActive(steps, [stack.length], `Pop '${popped}'`, `'${popped}' matches '${ch}' — pop it off the stack`)
      emit(steps, { type: 'replace-elements', elements: [...stack] })
    }
  }

  if (stack.length === 0) {
    emit(steps, { type: 'replace-elements', elements: str.split('') })
    setAnswer(steps, str.split('').map((_, i) => i), 'Valid ✓', `Every bracket matched and the stack is empty — "${str}" is valid ✓`)
  } else {
    setRejected(steps, stack.map((_, i) => i), 'Leftover brackets', `Stack not empty (${stack.join('')}) — unmatched opening brackets. INVALID`)
  }
  return steps
}

// ─── #67 Add Binary ─────────────────────────────────────────────────────────

export function addBinary(a = '1010', b = '1011') {
  const steps = []
  const maxLen = Math.max(a.length, b.length)
  const aP = a.padStart(maxLen, '0')
  const bP = b.padStart(maxLen, '0')
  // result array has maxLen+1 slots (extra for possible carry)
  const result = new Array(maxLen + 1).fill(0)
  initArray(steps, result, 'Initialize', `Add binary "${a}" + "${b}" right-to-left with carry`)
  setVars(steps, { a: `"${a}"`, b: `"${b}"`, carry: 0 }, 'Setup', 'Process from the rightmost bit leftward')

  let carry = 0
  for (let i = maxLen - 1; i >= 0; i--) {
    const da = parseInt(aP[i])
    const db = parseInt(bP[i])
    const prevCarry = carry
    const sum = da + db + prevCarry
    const bit = sum % 2
    carry = Math.floor(sum / 2)
    result[i + 1] = bit
    emit(steps, { type: 'replace-elements', elements: [...result] })
    setActive(steps, [i + 1], `Bit pos ${i}`, `a[${i}]=${da} + b[${i}]=${db} + carry_in=${prevCarry} = ${sum} → bit=${bit}, carry_out=${carry}`)
    setVars(steps, { [`a[${i}]`]: da, [`b[${i}]`]: db, carry_in: prevCarry, carry_out: carry, bit })
    setVisited(steps, [i + 1])
  }
  if (carry) {
    result[0] = 1
    emit(steps, { type: 'replace-elements', elements: [...result] })
    setActive(steps, [0], 'Final carry', `Remaining carry = 1 — place at position 0`)
    setVisited(steps, [0])
  }
  const resultStr = (carry ? result : result.slice(1)).join('')
  setAnswer(steps, result.map((_, i) => i), `"${resultStr}"`, `"${a}" + "${b}" = "${resultStr}" in binary`)
  return steps
}

// ─── #141 Linked List Cycle ──────────────────────────────────────────────────

export function linkedListCycle(values = [3, 2, 0, -4], cyclePos = 1) {
  const steps = []
  const n = values.length
  initArray(steps, values, 'Initialize',
    `Linked list [${values.join(' → ')}] has a cycle: tail → node at index ${cyclePos}. Detect using Floyd's algorithm.`)
  setVars(steps, { slow: 0, fast: 0 }, 'Init pointers', 'Both slow and fast start at head (index 0)')
  pointer(steps, 'slow', 0)
  pointer(steps, 'fast', 0)

  let slow = 0, fast = 0
  const maxIter = n * 3
  let found = false

  for (let step = 0; step < maxIter; step++) {
    slow = slow + 1 < n ? slow + 1 : cyclePos
    fast = fast + 1 < n ? fast + 1 : cyclePos
    fast = fast + 1 < n ? fast + 1 : cyclePos

    setActive(steps, [slow], `slow → ${slow}`, `slow moves one step to index ${slow} (value ${values[slow]})`)
    setCompared(steps, [fast], `fast → ${fast}`, `fast moves two steps to index ${fast} (value ${values[fast]})`)
    pointer(steps, 'slow', slow, { slow, fast })
    pointer(steps, 'fast', fast)
    setVars(steps, { slow, fast, 'slow.val': values[slow], 'fast.val': values[fast] })

    if (slow === fast) {
      setAnswer(steps, [slow], 'Cycle Detected! ✓',
        `slow and fast met at index ${slow} (value ${values[slow]}) — a cycle exists ✓`)
      found = true
      break
    }
    clearHighlights(steps)
  }
  if (!found) {
    setRejected(steps, values.map((_, i) => i), 'No Cycle', 'Pointers never met — no cycle in this list')
  }
  return steps
}

// ─── #232 Implement Queue using Stacks ──────────────────────────────────────

export function queueUsingStacks() {
  const ops = [
    { op: 'push', val: 1 },
    { op: 'push', val: 2 },
    { op: 'push', val: 3 },
    { op: 'peek' },
    { op: 'push', val: 4 },
    { op: 'pop' },
    { op: 'pop' },
  ]
  const steps = []
  initArray(steps, [], 'Initialize', 'Queue using two stacks: stack1 (input), stack2 (output). Transfer when stack2 is empty.')
  setVars(steps, { stack1: '[]', stack2: '[]' }, 'Setup', 'stack1 receives pushes; stack2 serves pops/peeks (elements in reverse order)')

  const s1 = [], s2 = []

  const sync = () => {
    emit(steps, { type: 'replace-elements', elements: [...s1, ...s2.slice().reverse()] })
    setVars(steps, { stack1: `[${s1.join(', ')}]`, stack2: `[${s2.join(', ')}]`, 'queue front': s2.length ? s2[s2.length - 1] : (s1[0] ?? '—') })
  }

  for (const { op, val } of ops) {
    if (op === 'push') {
      s1.push(val)
      sync()
      setActive(steps, [s1.length - 1], `Push ${val}`, `Enqueue ${val}: push onto stack1. stack1=[${s1.join(', ')}]`)
    } else if (op === 'pop' || op === 'peek') {
      if (s2.length === 0) {
        // Transfer s1 → s2
        while (s1.length) s2.push(s1.pop())
        sync()
        setCompared(steps, s2.map((_, i) => i), 'Transfer s1 → s2',
          `stack2 is empty — pour all of stack1 into stack2 to reverse order. stack2=[${s2.join(', ')}]`)
      }
      if (op === 'pop') {
        const v = s2.pop()
        sync()
        setAnswer(steps, [], `Pop → ${v}`, `Dequeue: pop top of stack2 = ${v}`)
      } else {
        setAnswer(steps, [s1.length + s2.length - 1], `Peek → ${s2[s2.length - 1]}`,
          `Front of queue is top of stack2 = ${s2[s2.length - 1]}`)
      }
    }
  }
  return steps
}

// ─── #242 Valid Anagram ──────────────────────────────────────────────────────

export function validAnagram(s = 'anagram', t = 'nagaram') {
  const steps = []
  const chars = [...new Set((s + t).split(''))].sort()
  const freq = new Array(chars.length).fill(0)
  initArray(steps, freq, 'Initialize',
    `Is "${s}" an anagram of "${t}"? Track char frequencies — all must be 0 after processing both strings.`)
  setVars(steps, { s: `"${s}"`, t: `"${t}"` }, 'Setup', 'Increment for each char in s, decrement for each char in t')

  const count = {}
  chars.forEach((c) => { count[c] = 0 })

  for (const ch of s) {
    if (count[ch] !== undefined) {
      count[ch]++
      const idx = chars.indexOf(ch)
      emit(steps, { type: 'replace-elements', elements: chars.map((c) => count[c]) })
      setActive(steps, [idx], `s: '${ch}' +1`, `'${ch}' in s — increment count to ${count[ch]}`)
      setVars(steps, { char: `'${ch}'`, count: count[ch] })
      setVisited(steps, [idx])
    }
  }

  for (const ch of t) {
    if (count[ch] !== undefined) {
      count[ch]--
      const idx = chars.indexOf(ch)
      emit(steps, { type: 'replace-elements', elements: chars.map((c) => count[c]) })
      setActive(steps, [idx], `t: '${ch}' -1`, `'${ch}' in t — decrement count to ${count[ch]}`)
      setVars(steps, { char: `'${ch}'`, count: count[ch] })
      setVisited(steps, [idx])
    }
  }

  const allZero = Object.values(count).every((v) => v === 0)
  const mismatch = chars.map((_, i) => i).filter((i) => count[chars[i]] !== 0)
  if (allZero) {
    setAnswer(steps, chars.map((_, i) => i), 'Valid Anagram ✓',
      `All frequency counts are 0 — "${s}" and "${t}" are anagrams ✓`)
  } else {
    setRejected(steps, mismatch, 'Not an Anagram ✗',
      `Counts are non-zero — "${s}" and "${t}" are NOT anagrams ✗`)
  }
  return steps
}

// ─── #383 Ransom Note ────────────────────────────────────────────────────────

export function ransomNote(note = 'aa', magazine = 'aab') {
  const steps = []
  const chars = [...new Set((note + magazine).split(''))].sort()
  const freq = new Array(chars.length).fill(0)
  initArray(steps, freq, 'Initialize',
    `Can "${note}" be built from "${magazine}"? Count magazine chars, then consume for note.`)
  setVars(steps, { note: `"${note}"`, magazine: `"${magazine}"` }, 'Setup',
    'Fill frequency counts from magazine, then decrement for each letter needed by the note')

  const count = {}
  chars.forEach((c) => { count[c] = 0 })

  for (const ch of magazine) {
    if (count[ch] !== undefined) {
      count[ch]++
      const idx = chars.indexOf(ch)
      emit(steps, { type: 'replace-elements', elements: chars.map((c) => count[c]) })
      setActive(steps, [idx], `mag: '${ch}' +1`, `'${ch}' in magazine: now ${count[ch]} available`)
      setVisited(steps, [idx])
    }
  }

  let possible = true
  for (const ch of note) {
    const idx = chars.indexOf(ch)
    if (count[ch] === undefined || count[ch] === 0) {
      setRejected(steps, idx >= 0 ? [idx] : [], 'Cannot Build Note ✗',
        `Need '${ch}' for the note but none left in magazine — impossible ✗`)
      possible = false
      break
    }
    count[ch]--
    emit(steps, { type: 'replace-elements', elements: chars.map((c) => count[c]) })
    setActive(steps, [idx], `note: '${ch}' -1`, `Use '${ch}' from magazine: ${count[ch]} remaining`)
    setVisited(steps, [idx])
  }

  if (possible) {
    setAnswer(steps, chars.map((_, i) => i), 'Ransom Note Possible ✓',
      `All letters needed are available in the magazine ✓`)
  }
  return steps
}

// ─── #409 Longest Palindrome ─────────────────────────────────────────────────

export function longestPalindrome(s = 'abccccdd') {
  const steps = []
  const chars = [...new Set(s.split(''))].sort()
  const freq = new Array(chars.length).fill(0)
  initArray(steps, freq, 'Initialize',
    `Longest palindrome from "${s}": pair up even-count chars; at most one odd-count char can sit in the center.`)
  setVars(steps, { s: `"${s}"` }, 'Setup', 'Count each character frequency')

  const count = {}
  chars.forEach((c) => { count[c] = 0 })

  for (const ch of s) {
    count[ch]++
    const idx = chars.indexOf(ch)
    emit(steps, { type: 'replace-elements', elements: chars.map((c) => count[c]) })
    setActive(steps, [idx], `'${ch}' count: ${count[ch]}`, `Increment '${ch}' to ${count[ch]}`)
    setVisited(steps, [idx])
  }

  let length = 0
  let hasOdd = false
  const paired = [], odd = []
  for (const c of chars) {
    const pairs = Math.floor(count[c] / 2)
    length += pairs * 2
    if (count[c] % 2 === 1) { hasOdd = true; odd.push(c) }
    if (pairs > 0) paired.push(c)
  }
  if (hasOdd) length++

  setAnswer(steps, chars.map((_, i) => i), `Length = ${length}`,
    `Pairs from [${paired.join(', ')}] contribute ${length - (hasOdd ? 1 : 0)} chars. ${hasOdd ? `One of [${odd.join(', ')}] sits in the center. ` : ''}Longest palindrome: ${length}`)
  return steps
}

// ─── #876 Middle of the Linked List ─────────────────────────────────────────

export function middleLinkedList(values = [1, 2, 3, 4, 5]) {
  const steps = []
  const n = values.length
  initArray(steps, values, 'Initialize',
    `Find the middle of [${values.join(' → ')}] using slow/fast pointers (tortoise and hare).`)
  setVars(steps, { slow: 0, fast: 0 }, 'Init', 'Both slow and fast start at head')
  pointer(steps, 'slow', 0)
  pointer(steps, 'fast', 0)

  let slow = 0, fast = 0
  while (fast < n - 1) {
    fast = Math.min(fast + 2, n - 1)
    slow = Math.min(slow + 1, n - 1)
    setActive(steps, [slow], `slow → ${slow}`, `slow moves one step: node ${values[slow]}`)
    setCompared(steps, [fast], `fast → ${fast}`, `fast moves two steps: node ${values[fast]}`)
    pointer(steps, 'slow', slow, { slow, fast })
    pointer(steps, 'fast', fast)
    setVars(steps, { slow, fast, 'slow.val': values[slow], 'fast.val': values[fast] })
    clearHighlights(steps)
  }
  setAnswer(steps, [slow], `Middle at index ${slow}`,
    `fast reached the end — slow is at the middle: index ${slow}, value ${values[slow]}`)
  return steps
}

// ─── #21 Merge Two Sorted Lists ─────────────────────────────────────────────

export function mergeTwoSortedLists(l1 = [1, 2, 4], l2 = [1, 3, 4]) {
  const steps = []
  const combined = [...l1, null, ...l2]  // null acts as separator in display
  const merged = []
  initArray(steps, [...l1, ...l2], 'Initialize', `Merge sorted lists [${l1.join('→')}] and [${l2.join('→')}] into one sorted list.`)
  setVars(steps, { p1: 0, p2: 0, 'list1': `[${l1.join(',')}]`, 'list2': `[${l2.join(',')}]` }, 'Setup', 'Two pointers, one per list. Pick the smaller head each step.')

  let i = 0, j = 0
  while (i < l1.length && j < l2.length) {
    setActive(steps, [i], `p1: ${l1[i]}`, `Comparing l1[${i}]=${l1[i]} vs l2[${j}]=${l2[j]}`)
    setCompared(steps, [l1.length + j], `p2: ${l2[j]}`, `Which is smaller?`)
    pointer(steps, 'p1', i, { p1: l1[i], p2: l2[j] })
    pointer(steps, 'p2', l1.length + j)
    if (l1[i] <= l2[j]) {
      merged.push(l1[i])
      setVars(steps, { merged: `[${merged.join(',')}]` }, `pick ${l1[i]}`, `${l1[i]} ≤ ${l2[j]} — take from list1`)
      setAnswer(steps, [i], `Take ${l1[i]}`, `Append ${l1[i]} to merged`)
      i++
    } else {
      merged.push(l2[j])
      setVars(steps, { merged: `[${merged.join(',')}]` }, `pick ${l2[j]}`, `${l2[j]} < ${l1[i]} — take from list2`)
      setAnswer(steps, [l1.length + j], `Take ${l2[j]}`, `Append ${l2[j]} to merged`)
      j++
    }
    clearHighlights(steps)
  }
  while (i < l1.length) { merged.push(l1[i]); setAnswer(steps, [i], `Append ${l1[i]}`, `list2 exhausted — append remaining list1`); i++ }
  while (j < l2.length) { merged.push(l2[j]); setAnswer(steps, [l1.length + j], `Append ${l2[j]}`, `list1 exhausted — append remaining list2`); j++ }

  emit(steps, { type: 'replace-elements', elements: merged })
  setAnswer(steps, merged.map((_, k) => k), 'Merged ✓', `Result: [${merged.join(' → ')}]`)
  return steps
}

// ─── #3 Longest Substring Without Repeating Characters ──────────────────────

export function longestSubstringNoRepeat(s = 'abcabcbb') {
  const steps = []
  const chars = s.split('')
  initArray(steps, chars, 'Initialize', `Find the longest substring of "${s}" with no repeated characters.`)
  setVars(steps, { left: 0, right: 0, maxLen: 0, window: '""' }, 'Setup', 'Sliding window: expand right, shrink left on duplicate.')

  let left = 0, maxLen = 0, bestL = 0, bestR = -1
  const seen = new Map()
  for (let right = 0; right < chars.length; right++) {
    const ch = chars[right]
    setActive(steps, [right], `right=${right} '${ch}'`, `Expand window to include '${ch}'`)
    pointer(steps, 'R', right, { right, char: `'${ch}'` })

    if (seen.has(ch) && seen.get(ch) >= left) {
      const dup = seen.get(ch)
      setRejected(steps, [dup], `Duplicate '${ch}'`, `'${ch}' already in window at ${dup} — move left past it`)
      left = dup + 1
      pointer(steps, 'L', left, { left })
    }
    seen.set(ch, right)
    const len = right - left + 1
    if (len > maxLen) { maxLen = len; bestL = left; bestR = right }
    const winChars = chars.slice(left, right + 1).join('')
    setVars(steps, { left, right, window: `"${winChars}"`, len, maxLen }, `len=${len}`, `Window "${winChars}" has ${len} unique chars${len === maxLen ? ' — new best!' : ''}`)
    clearHighlights(steps)
    setVisited(steps, Array.from({ length: right - left + 1 }, (_, k) => left + k), `window [${left}..${right}]`, `Current valid window`)
  }
  setAnswer(steps, Array.from({ length: bestR - bestL + 1 }, (_, k) => bestL + k), `maxLen=${maxLen}`, `Longest substring: "${s.slice(bestL, bestR + 1)}" (length ${maxLen})`)
  return steps
}

// ─── #5 Longest Palindromic Substring ───────────────────────────────────────

export function longestPalindromicSubstring(s = 'babad') {
  const steps = []
  const chars = s.split('')
  initArray(steps, chars, 'Initialize', `Find the longest palindromic substring of "${s}" by expanding around each center.`)
  setVars(steps, { best: '""', bestLen: 0 }, 'Setup', 'For each index, expand outward while chars match.')

  let bestStart = 0, bestLen = 1

  function expand(l, r, label) {
    setCompared(steps, [l, r], label, `Try expanding from center ${label}`)
    while (l >= 0 && r < chars.length && chars[l] === chars[r]) {
      setActive(steps, [l, r], `[${l}..${r}] '${s.slice(l, r + 1)}'`, `chars[${l}]='${chars[l]}' === chars[${r}]='${chars[r]}' — expand!`)
      const len = r - l + 1
      if (len > bestLen) {
        bestLen = len; bestStart = l
        setVars(steps, { best: `"${s.slice(l, r + 1)}"`, bestLen }, `new best`, `Longest palindrome so far: "${s.slice(l, r + 1)}"`)
      }
      l--; r++
    }
    clearHighlights(steps)
  }

  for (let i = 0; i < chars.length; i++) {
    pointer(steps, 'i', i, { i })
    expand(i, i, `${i} (odd)`)
    if (i + 1 < chars.length) expand(i, i + 1, `${i}-${i+1} (even)`)
  }
  setAnswer(steps, Array.from({ length: bestLen }, (_, k) => bestStart + k), `"${s.slice(bestStart, bestStart + bestLen)}"`, `Longest palindromic substring: "${s.slice(bestStart, bestStart + bestLen)}" (length ${bestLen})`)
  return steps
}

// ─── #11 Container With Most Water ──────────────────────────────────────────

export function containerWithMostWater(heights = [1, 8, 6, 2, 5, 4, 8, 3, 7]) {
  const steps = []
  initArray(steps, heights, 'Initialize', `Max water container from heights [${heights.join(', ')}]. Two pointers, move the shorter side.`)
  let l = 0, r = heights.length - 1, maxWater = 0, bestL = 0, bestR = heights.length - 1
  pointer(steps, 'L', l)
  pointer(steps, 'R', r)
  setVars(steps, { L: l, R: r, maxWater: 0 }, 'Init', 'Start with widest container, shrink from the shorter wall.')

  while (l < r) {
    const water = Math.min(heights[l], heights[r]) * (r - l)
    setCompared(steps, [l, r], `water = ${water}`, `min(${heights[l]},${heights[r]}) × (${r}-${l}) = ${water}`)
    pointer(steps, 'L', l, { L: l, R: r, water })
    pointer(steps, 'R', r)
    if (water > maxWater) { maxWater = water; bestL = l; bestR = r }
    setVars(steps, { L: l, R: r, water, maxWater }, `water=${water}`, `${water > maxWater - water === 0 ? 'new max!' : ''}Water=${water}, maxWater=${maxWater}`)
    if (heights[l] <= heights[r]) {
      setRejected(steps, [l], `move L`, `heights[L]=${heights[l]} ≤ heights[R]=${heights[r]}, move left pointer right`)
      l++
    } else {
      setRejected(steps, [r], `move R`, `heights[R]=${heights[r]} < heights[L]=${heights[l]}, move right pointer left`)
      r--
    }
    clearHighlights(steps)
  }
  setAnswer(steps, [bestL, bestR], `maxWater=${maxWater}`, `Best container: L=${bestL}, R=${bestR}, water=${maxWater}`)
  return steps
}

// ─── #15 3Sum ────────────────────────────────────────────────────────────────

export function threeSum(nums = [-1, 0, 1, 2, -1, -4]) {
  const steps = []
  const a = [...nums].sort((x, y) => x - y)
  initArray(steps, a, 'Initialize', `3Sum on sorted [${a.join(', ')}]. Fix one element, use two pointers for the rest.`)
  setVars(steps, { sorted: `[${a.join(',')}]` }, 'Sort', 'Sort the array first — required for two-pointer approach.')

  const results = []
  for (let i = 0; i < a.length - 2; i++) {
    if (i > 0 && a[i] === a[i - 1]) {
      setRejected(steps, [i], `skip dup ${a[i]}`, `a[${i}]=${a[i]} same as previous — skip to avoid duplicate triplets`)
      continue
    }
    let l = i + 1, r = a.length - 1
    pointer(steps, 'i', i, { i, 'a[i]': a[i] })
    setActive(steps, [i], `fix i=${i}`, `Fix a[${i}]=${a[i]}, two-pointer on rest`)

    while (l < r) {
      const sum = a[i] + a[l] + a[r]
      setCompared(steps, [l, r], `sum=${sum}`, `${a[i]}+${a[l]}+${a[r]}=${sum}`)
      pointer(steps, 'L', l, { L: l, R: r, sum })
      pointer(steps, 'R', r)
      if (sum === 0) {
        results.push([a[i], a[l], a[r]])
        setAnswer(steps, [i, l, r], `Found [${a[i]},${a[l]},${a[r]}]`, `Sum is 0 ✓ — found a triplet!`)
        setVars(steps, { results: results.map(t => `[${t.join(',')}]`).join(' ') }, 'result', `Added [${a[i]},${a[l]},${a[r]}]`)
        while (l < r && a[l] === a[l + 1]) l++
        while (l < r && a[r] === a[r - 1]) r--
        l++; r--
      } else if (sum < 0) {
        setRejected(steps, [l], `sum<0 move L`, `Sum ${sum} < 0 — need larger, move L right`)
        l++
      } else {
        setRejected(steps, [r], `sum>0 move R`, `Sum ${sum} > 0 — need smaller, move R left`)
        r--
      }
      clearHighlights(steps)
    }
    clearHighlights(steps)
  }
  if (results.length === 0) {
    setRejected(steps, a.map((_, k) => k), 'No triplets', 'No zero-sum triplets found')
  }
  return steps
}

// ─── #33 Search in Rotated Sorted Array ─────────────────────────────────────

export function searchRotatedSortedArray(nums = [4, 5, 6, 7, 0, 1, 2], target = 0) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Search for ${target} in rotated sorted array [${nums.join(', ')}].`)
  let l = 0, r = nums.length - 1
  pointer(steps, 'L', l)
  pointer(steps, 'R', r)
  setVars(steps, { L: l, R: r, target }, 'Init', 'Binary search — determine which half is sorted, then decide which to search.')

  while (l <= r) {
    const mid = (l + r) >> 1
    setActive(steps, [mid], `mid=${mid}`, `mid index ${mid}, value ${nums[mid]}`)
    pointer(steps, 'M', mid, { L: l, M: mid, R: r, 'nums[mid]': nums[mid] })
    if (nums[mid] === target) {
      setAnswer(steps, [mid], `Found at ${mid}`, `nums[${mid}]=${nums[mid]} === target ${target} ✓`)
      return steps
    }
    if (nums[l] <= nums[mid]) {
      setVars(steps, { note: 'left half sorted' }, `[L..mid] sorted`, `nums[${l}]=${nums[l]} ≤ nums[${mid}]=${nums[mid]} — left half is sorted`)
      if (nums[l] <= target && target < nums[mid]) {
        setRejected(steps, Array.from({ length: nums.length - mid - 1 }, (_, k) => mid + 1 + k), 'search left', `Target ${target} in [${nums[l]}..${nums[mid]-1}] — eliminate right`)
        r = mid - 1
      } else {
        setRejected(steps, Array.from({ length: mid - l + 1 }, (_, k) => l + k), 'search right', `Target ${target} not in left half — eliminate left`)
        l = mid + 1
      }
    } else {
      setVars(steps, { note: 'right half sorted' }, `[mid..R] sorted`, `Right half is sorted`)
      if (nums[mid] < target && target <= nums[r]) {
        setRejected(steps, Array.from({ length: mid - l + 1 }, (_, k) => l + k), 'search right', `Target ${target} in right sorted half`)
        l = mid + 1
      } else {
        setRejected(steps, Array.from({ length: nums.length - mid }, (_, k) => mid + k), 'search left', `Target ${target} not in right half`)
        r = mid - 1
      }
    }
    clearHighlights(steps)
    pointer(steps, 'L', l, { L: l, R: r })
    pointer(steps, 'R', r)
  }
  setRejected(steps, nums.map((_, k) => k), `Not found`, `Target ${target} not in array — return -1`)
  return steps
}

// ─── #53 Maximum Subarray (Kadane's) ────────────────────────────────────────

export function maximumSubarray(nums = [-2, 1, -3, 4, -1, 2, 1, -5, 4]) {
  const steps = []
  initArray(steps, nums, 'Initialize', `Maximum subarray sum in [${nums.join(', ')}] — Kadane's algorithm.`)
  setVars(steps, { current: nums[0], maxSum: nums[0], start: 0 }, 'Init', 'current = running sum; drop it if it goes negative.')

  let curr = nums[0], maxSum = nums[0], bestL = 0, bestR = 0, currL = 0
  setActive(steps, [0], `curr=${curr}`, `Start with first element`)
  pointer(steps, 'i', 0, { current: curr, maxSum })

  for (let i = 1; i < nums.length; i++) {
    if (curr < 0) {
      setRejected(steps, [i - 1], `reset`, `current=${curr} is negative — reset, start new subarray at ${i}`)
      curr = nums[i]; currL = i
    } else {
      curr += nums[i]
    }
    if (curr > maxSum) { maxSum = curr; bestL = currL; bestR = i }
    setActive(steps, [i], `i=${i}`, `Add ${nums[i]}: current = ${curr}`)
    pointer(steps, 'i', i, { i, 'nums[i]': nums[i], current: curr, maxSum })
    setVars(steps, { current: curr, maxSum }, `current=${curr}`, `${curr === maxSum ? 'New max! ' : ''}maxSum=${maxSum}`)
    clearHighlights(steps)
  }
  setAnswer(steps, Array.from({ length: bestR - bestL + 1 }, (_, k) => bestL + k), `maxSum=${maxSum}`, `Max subarray [${bestL}..${bestR}]: ${nums.slice(bestL, bestR + 1).join('+')}=${maxSum}`)
  return steps
}

// ─── #56 Merge Intervals ────────────────────────────────────────────────────

export function mergeIntervals(intervals = [[1,3],[2,6],[8,10],[15,18]]) {
  const steps = []
  const sorted = [...intervals].sort((a, b) => a[0] - b[0])
  const flat = sorted.map(iv => `[${iv[0]},${iv[1]}]`).join(' ')
  initArray(steps, sorted.map(iv => iv[0]), 'Initialize', `Merge overlapping intervals: ${flat}`)
  setVars(steps, { intervals: flat }, 'Sort', 'Sort by start time. Merge if current overlaps with last merged.')

  const merged = [sorted[0]]
  setActive(steps, [0], `start [${sorted[0]}]`, `First interval [${sorted[0][0]},${sorted[0][1]}] added to result`)
  setVars(steps, { merged: `[[${sorted[0]}]]` }, 'Add first', '')

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    const curr = sorted[i]
    setActive(steps, [i], `check [${curr}]`, `Current [${curr[0]},${curr[1]}] vs last merged [${last[0]},${last[1]}]`)
    pointer(steps, 'i', i, { 'curr[0]': curr[0], 'curr[1]': curr[1], 'last[1]': last[1] })
    if (curr[0] <= last[1]) {
      last[1] = Math.max(last[1], curr[1])
      setAnswer(steps, [i], `Merge → [${last}]`, `${curr[0]} ≤ ${last[1]} — overlap! Extend to [${last[0]},${last[1]}]`)
    } else {
      merged.push([...curr])
      setVisited(steps, [i], `No overlap`, `${curr[0]} > ${last[1]} — no overlap, add as new interval`)
    }
    setVars(steps, { merged: merged.map(iv => `[${iv}]`).join(' ') }, '', '')
    clearHighlights(steps)
  }
  setAnswer(steps, Array.from({ length: sorted.length }, (_, k) => k), `${merged.length} intervals`, `Result: ${merged.map(iv => `[${iv[0]},${iv[1]}]`).join(' ')}`)
  return steps
}

// ─── #57 Insert Interval ────────────────────────────────────────────────────

export function insertInterval(intervals = [[1,3],[6,9]], newInterval = [2,5]) {
  const steps = []
  initArray(steps, intervals.map(iv => iv[0]), 'Initialize', `Insert [${newInterval}] into [[${intervals.map(iv=>iv.join(',')).join('],[')}]].`)
  setVars(steps, { newInterval: `[${newInterval}]` }, 'Setup', 'Add all intervals before new one, merge overlaps, add remainder.')

  const result = []
  let i = 0, ni = [...newInterval]

  while (i < intervals.length && intervals[i][1] < ni[0]) {
    result.push(intervals[i])
    setVisited(steps, [i], `before`, `[${intervals[i]}] ends before new starts — add as-is`)
    setVars(steps, { result: result.map(iv=>`[${iv}]`).join(' ') }, '', '')
    i++
  }
  while (i < intervals.length && intervals[i][0] <= ni[1]) {
    ni[0] = Math.min(ni[0], intervals[i][0])
    ni[1] = Math.max(ni[1], intervals[i][1])
    setActive(steps, [i], `merge`, `[${intervals[i]}] overlaps — expand new to [${ni}]`)
    setVars(steps, { newInterval: `[${ni}]` }, '', '')
    i++
  }
  result.push(ni)
  setAnswer(steps, [result.length - 1], `merged [${ni}]`, `Merged new interval is [${ni[0]},${ni[1]}]`)
  while (i < intervals.length) {
    result.push(intervals[i])
    setVisited(steps, [i], `after`, `[${intervals[i]}] starts after — add as-is`)
    i++
  }
  setVars(steps, { result: result.map(iv=>`[${iv}]`).join(' ') }, 'Done', `Result: ${result.map(iv=>`[${iv[0]},${iv[1]}]`).join(' ')}`)
  return steps
}

// ─── #75 Sort Colors (Dutch National Flag) ──────────────────────────────────

export function sortColors(nums = [2, 0, 2, 1, 1, 0]) {
  const steps = []
  const a = [...nums]
  initArray(steps, a, 'Initialize', `Sort Colors (Dutch National Flag) on [${a.join(', ')}]. One pass, three pointers.`)
  let lo = 0, mid = 0, hi = a.length - 1
  pointer(steps, 'lo', lo)
  pointer(steps, 'mid', mid)
  pointer(steps, 'hi', hi)
  setVars(steps, { lo, mid, hi }, 'Init', 'lo: boundary of 0s; hi: boundary of 2s; mid: current element.')

  while (mid <= hi) {
    setActive(steps, [mid], `a[mid]=${a[mid]}`, `Inspect a[${mid}]=${a[mid]}`)
    if (a[mid] === 0) {
      ;[a[lo], a[mid]] = [a[mid], a[lo]]
      doSwap(steps, lo, mid, `swap lo↔mid`, `0 found — swap with lo, advance both lo and mid`)
      lo++; mid++
    } else if (a[mid] === 1) {
      setVisited(steps, [mid], `1 in place`, `1 is already in the middle region — just advance mid`)
      mid++
    } else {
      ;[a[mid], a[hi]] = [a[hi], a[mid]]
      doSwap(steps, mid, hi, `swap mid↔hi`, `2 found — swap with hi, shrink hi (don't advance mid)`)
      hi--
    }
    clearHighlights(steps)
    pointer(steps, 'lo', lo, { lo, mid, hi })
    pointer(steps, 'mid', mid)
    pointer(steps, 'hi', hi)
  }
  setAnswer(steps, a.map((_, k) => k), 'Sorted ✓', `[${a.join(', ')}] sorted in one pass ✓`)
  return steps
}

// ─── #150 Evaluate Reverse Polish Notation ──────────────────────────────────

export function evalRPN(tokens = ['2','1','+','3','*']) {
  const steps = []
  initArray(steps, [], 'Initialize', `Evaluate RPN: [${tokens.join(', ')}]. Push numbers, apply operator to top two.`)
  setVars(steps, { tokens: tokens.join(' '), stack: '[]' }, 'Setup', 'Stack-based evaluation: push numbers; on operator pop two, compute, push result.')

  const stack = []
  const sync = () => emit(steps, { type: 'replace-elements', elements: [...stack] })

  for (const tok of tokens) {
    if (['+', '-', '*', '/'].includes(tok)) {
      const b = stack.pop(), a = stack.pop()
      sync()
      setCompared(steps, [stack.length, stack.length + 1], `pop ${a}, ${b}`, `Operator '${tok}': pop ${b} then ${a}`)
      const res = tok === '+' ? a + b : tok === '-' ? a - b : tok === '*' ? a * b : Math.trunc(a / b)
      stack.push(res)
      sync()
      setAnswer(steps, [stack.length - 1], `${a}${tok}${b}=${res}`, `${a} ${tok} ${b} = ${res} — push result`)
      setVars(steps, { stack: `[${stack.join(', ')}]` }, `result ${res}`, '')
    } else {
      stack.push(Number(tok))
      sync()
      setActive(steps, [stack.length - 1], `push ${tok}`, `Number token ${tok} — push onto stack`)
      setVars(steps, { stack: `[${stack.join(', ')}]` }, `push ${tok}`, '')
    }
  }
  setAnswer(steps, [0], `Result = ${stack[0]}`, `Final result: ${stack[0]}`)
  return steps
}

// ─── #155 Min Stack ──────────────────────────────────────────────────────────

export function minStack(ops = [['push',5],['push',3],['push',7],['push',1],['getMin'],['pop'],['getMin'],['push',2],['getMin']]) {
  const steps = []
  initArray(steps, [], 'Initialize', 'Min Stack: push, pop, and getMin all in O(1) using an auxiliary min-stack.')
  setVars(steps, { stack: '[]', minStack: '[]' }, 'Setup', 'Main stack + parallel min-stack that tracks the current minimum.')

  const stack = [], minSt = []
  const sync = () => emit(steps, { type: 'replace-elements', elements: [...stack] })

  for (const [op, val] of ops) {
    if (op === 'push') {
      stack.push(val)
      minSt.push(Math.min(val, minSt.length ? minSt[minSt.length - 1] : val))
      sync()
      setActive(steps, [stack.length - 1], `push(${val})`, `Push ${val}. Min-stack top = ${minSt[minSt.length - 1]}`)
      setVars(steps, { stack: `[${stack.join(',')}]`, minStack: `[${minSt.join(',')}]`, min: minSt[minSt.length - 1] }, '', '')
    } else if (op === 'pop') {
      const v = stack.pop(); minSt.pop()
      sync()
      setRejected(steps, [], `pop → ${v}`, `Popped ${v}. New min = ${minSt.length ? minSt[minSt.length - 1] : 'none'}`)
      setVars(steps, { stack: `[${stack.join(',')}]`, minStack: `[${minSt.join(',')}]`, min: minSt.length ? minSt[minSt.length - 1] : '-' }, '', '')
    } else {
      const m = minSt[minSt.length - 1]
      setAnswer(steps, [stack.indexOf(m)], `getMin()=${m}`, `Current minimum is ${m} — O(1) lookup from min-stack top`)
    }
  }
  return steps
}

// ─── #238 Product of Array Except Self ──────────────────────────────────────

export function productExceptSelf(nums = [1, 2, 3, 4]) {
  const steps = []
  const n = nums.length
  initArray(steps, nums, 'Initialize', `Product of array except self: [${nums.join(', ')}]. Two passes — prefix then suffix.`)
  setVars(steps, { pass: 'prefix' }, 'Setup', 'Pass 1: fill output with prefix products. Pass 2: multiply by suffix products.')

  const output = new Array(n).fill(1)
  let prefix = 1
  for (let i = 0; i < n; i++) {
    output[i] = prefix
    setActive(steps, [i], `prefix[${i}]=${prefix}`, `output[${i}] = prefix product ${prefix}`)
    prefix *= nums[i]
    setVars(steps, { prefix, 'output': `[${output.join(',')}]` }, `prefix×${nums[i]}=${prefix}`, '')
    clearHighlights(steps)
  }
  emit(steps, { type: 'replace-elements', elements: [...output] })
  setVars(steps, { pass: 'suffix', suffix: 1 }, 'Pass 2', 'Now multiply each output[i] by the suffix product (right to left).')

  let suffix = 1
  for (let i = n - 1; i >= 0; i--) {
    output[i] *= suffix
    setActive(steps, [i], `suffix[${i}]=${suffix}`, `output[${i}] *= suffix ${suffix} → ${output[i]}`)
    suffix *= nums[i]
    emit(steps, { type: 'replace-elements', elements: [...output] })
    setVars(steps, { suffix, output: `[${output.join(',')}]` }, `suffix×${nums[i]}=${suffix}`, '')
    clearHighlights(steps)
  }
  setAnswer(steps, output.map((_, k) => k), `[${output.join(', ')}]`, `Each element is the product of all others: [${output.join(', ')}]`)
  return steps
}

// ─── #438 Find All Anagrams in a String ─────────────────────────────────────

export function findAllAnagrams(s = 'cbaebabacd', p = 'abc') {
  const steps = []
  const chars = s.split('')
  initArray(steps, chars, 'Initialize', `Find all positions where "${p}" appears as an anagram in "${s}".`)
  setVars(steps, { p, window: s.slice(0, p.length), matches: '[]' }, 'Setup', `Fixed-size window of length ${p.length}. Compare char frequencies.`)

  const k = p.length, n = s.length
  const need = new Array(26).fill(0)
  const have = new Array(26).fill(0)
  const a = 'a'.charCodeAt(0)
  for (const c of p) need[c.charCodeAt(0) - a]++
  for (let i = 0; i < k; i++) have[s.charCodeAt(i) - a]++

  const matches = []
  const isMatch = () => need.every((v, i) => v === have[i])

  for (let i = 0; i <= n - k; i++) {
    const winChars = Array.from({ length: k }, (_, x) => i + x)
    setActive(steps, winChars, `window [${i}..${i+k-1}]`, `Check "${s.slice(i, i+k)}" against "${p}"`)
    pointer(steps, 'L', i, { L: i, R: i + k - 1, window: `"${s.slice(i, i+k)}"` })

    if (isMatch()) {
      matches.push(i)
      setAnswer(steps, winChars, `Anagram at ${i}`, `"${s.slice(i, i+k)}" is an anagram of "${p}" ✓`)
      setVars(steps, { matches: `[${matches.join(', ')}]` }, `found at ${i}`, '')
    } else {
      clearHighlights(steps)
    }
    if (i + k < n) {
      have[s.charCodeAt(i) - a]--
      have[s.charCodeAt(i + k) - a]++
    }
  }
  if (matches.length === 0) setRejected(steps, chars.map((_, k) => k), 'No anagrams', `No anagram of "${p}" found in "${s}"`)
  return steps
}

// ─── #17 Letter Combinations of a Phone Number ──────────────────────────────

export function letterCombinations(digits = '23') {
  const steps = []
  const MAP = { '2':'abc','3':'def','4':'ghi','5':'jkl','6':'mno','7':'pqrs','8':'tuv','9':'wxyz' }
  const allLetters = digits.split('').flatMap(d => MAP[d].split(''))
  initArray(steps, allLetters, 'Initialize', `Letter combinations for "${digits}". Backtrack through each digit's letters.`)
  setVars(steps, { digits, combinations: '[]' }, 'Setup', 'Map digits to letters, build combinations via DFS.')

  const results = []
  function dfs(idx, path) {
    if (idx === digits.length) {
      results.push(path)
      setAnswer(steps, [], `Found "${path}"`, `Complete combination: "${path}"`)
      setVars(steps, { current: `"${path}"`, count: results.length, combinations: results.length > 5 ? `[...${results.length}]` : `[${results.map(r=>`"${r}"`).join(',')}]` }, '', '')
      return
    }
    for (const c of MAP[digits[idx]]) {
      const letterIdx = allLetters.indexOf(c)
      setActive(steps, letterIdx >= 0 ? [letterIdx] : [], `try '${c}'`, `Digit '${digits[idx]}' → letter '${c}'. Path so far: "${path+c}"`)
      dfs(idx + 1, path + c)
      clearHighlights(steps)
    }
  }
  if (digits.length) dfs(0, '')
  setVars(steps, { total: results.length, combinations: results.length > 8 ? `[...${results.length} total]` : `[${results.map(r=>`"${r}"`).join(',')}]` }, 'Done', `Found ${results.length} combinations`)
  return steps
}

// ─── #621 Task Scheduler ────────────────────────────────────────────────────

export function taskScheduler(tasks = ['A','A','A','B','B','B'], n = 2) {
  const steps = []
  const freq = {}
  for (const t of tasks) freq[t] = (freq[t] || 0) + 1
  const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1])
  const labels = sorted.map(([t]) => t)
  const counts = sorted.map(([, c]) => c)
  initArray(steps, counts, 'Initialize', `Task Scheduler: [${tasks.join(',')}] with cooldown n=${n}. Find min CPU intervals.`)
  setVars(steps, { tasks: tasks.join(''), n, frequencies: sorted.map(([t,c])=>`${t}:${c}`).join(' ') }, 'Count', 'Count frequencies, sort descending. Formula: (maxFreq-1)*(n+1) + countOfMaxFreq.')

  const maxFreq = counts[0]
  const maxCount = counts.filter(c => c === maxFreq).length
  const result = Math.max(tasks.length, (maxFreq - 1) * (n + 1) + maxCount)

  setActive(steps, [0], `maxFreq=${maxFreq}`, `Most frequent task appears ${maxFreq} times`)
  setVars(steps, { maxFreq, maxCount, formula: `(${maxFreq}-1)*(${n}+1)+${maxCount}`, calculated: (maxFreq-1)*(n+1)+maxCount, taskCount: tasks.length }, 'Formula', `(maxFreq-1)*(n+1)+maxCount = ${(maxFreq-1)*(n+1)+maxCount}`)
  setAnswer(steps, counts.map((_, k) => k), `minIntervals=${result}`, `min(tasks.length, formula) = min(${tasks.length}, ${(maxFreq-1)*(n+1)+maxCount}) = ${result}`)
  return steps
}

// ─── #206 Reverse Linked List ───────────────────────────────────────────────

export function reverseLinkedList(values) {
  const steps = []
  initArray(steps, values, 'Initialize', `Reverse the list ${values.join(' → ')} → null by re-pointing each node`)
  setVars(steps, { prev: 'null', curr: `node(${values[0]})` }, 'Init pointers', 'prev = null, curr = head')

  for (let i = 0; i < values.length; i++) {
    setActive(steps, [i], `curr = ${values[i]}`, `Re-point node ${values[i]} to its predecessor (${i === 0 ? 'null' : values[i - 1]})`)
    pointer(steps, 'curr', i, {
      prev: i === 0 ? 'null' : `node(${values[i - 1]})`,
      curr: `node(${values[i]})`,
      next: i < values.length - 1 ? `node(${values[i + 1]})` : 'null',
    })
    if (i > 0) pointer(steps, 'prev', i - 1)
    setVisited(steps, [i], `${values[i]} re-pointed`, `Node ${values[i]} now points back to ${i === 0 ? 'null' : values[i - 1]}`)
  }

  const rev = [...values].reverse()
  emit(steps, { type: 'replace-elements', elements: rev })
  setAnswer(steps, rev.map((_, i) => i), 'Reversed ✓', `New head is ${rev[0]} — the list is now ${rev.join(' → ')} → null`)
  return steps
}
